import { useCallback, useMemo, useState } from 'react';

import { type Seller } from '@/infra/services/catalog/product/get-sellers/model';
import { useAddProductsToCart } from '@/presenters/hooks/checkout/cart';
import { type UseAddProductsToCartProps } from '@/presenters/hooks/checkout/cart/use-add-products-to-cart/use-add-products-to-cart';
import { useProductStore } from '@/presenters/store/catalog/product';
import { CART_ID } from '@/utils/constants/cookies';
import { cookies } from '@/utils/cookies';

import { IGNORED_VOLTAGES } from './constants';

interface UseActionsProps
  extends Pick<
    UseAddProductsToCartProps,
    'onError' | 'onSuccess' | 'postSuccess'
  > {
  seller?: Seller;
  quantity: number;
}

export default function useActions(props: Readonly<UseActionsProps>) {
  const {
    onError,
    onSuccess,
    seller: defaultSeller,
    quantity,
    postSuccess,
  } = props;

  const [voltageDialogIsOpen, setVoltageDialogIsOpen] = useState(false);

  const {
    currentSeller,
    product: { _id: productId, characteristics },
  } = useProductStore((store) => store);

  const seller = defaultSeller || currentSeller;

  const shouldShowVoltageModal = useMemo(() => {
    if (!characteristics) return false;

    return characteristics?.some(
      ({ name, value }) =>
        name === 'Tensão Elétrica' && !IGNORED_VOLTAGES.includes(value)
    );
  }, [characteristics]);

  const { mutate, isLoading } = useAddProductsToCart({
    onSuccess,
    onError,
    postSuccess,
  });

  const addToCart = useCallback(() => {
    mutate({
      products: [{ id: productId, quantity, skuId: seller.id }],
      type: 'delivery',
      override: false,
      cartId: cookies.client.get(CART_ID)?.value || '',
    });
  }, [mutate, productId, quantity, seller.id]);

  const onConfirm = () => {
    setVoltageDialogIsOpen(false);
    addToCart();
  };

  const onClick = () => {
    if (!shouldShowVoltageModal) {
      addToCart();
    }
  };

  const isDisabled = !seller.ecommerceScopes.canPurchase;

  return {
    isDisabled,
    onClick,
    onConfirm,
    voltageDialogIsOpen,
    setVoltageDialogIsOpen,
    isLoading,
    shouldShowVoltageModal,
  };
}
